const THEME_KEY = "theme";

export type ThemeMode = "light" | "dark";

const isBrowser = typeof window !== "undefined";

export const getSystemTheme = (): ThemeMode => {
  if (!isBrowser) return "dark";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

export const getStoredTheme = (): ThemeMode | null => {
  if (!isBrowser) return null;
  const stored = window.localStorage.getItem(THEME_KEY);
  return stored === "dark" || stored === "light" ? stored : null;
};

export const resolveInitialTheme = (): ThemeMode => getStoredTheme() ?? getSystemTheme();

export const applyTheme = (theme: ThemeMode) => {
  if (!isBrowser) return;
  document.documentElement.classList.toggle("dark", theme === "dark");
  document.documentElement.style.colorScheme = theme;
};

export const persistTheme = (theme: ThemeMode) => {
  if (!isBrowser) return;
  window.localStorage.setItem(THEME_KEY, theme);
};

export const initializeTheme = () => {
  const theme = resolveInitialTheme();
  applyTheme(theme);
  return theme;
};

export const getDocumentTheme = (): ThemeMode => {
  if (!isBrowser) return "dark";
  return document.documentElement.classList.contains("dark") ? "dark" : "light";
};

export const ensureThemeSynchronization = () => {
  if (!isBrowser) return;
  const expected = resolveInitialTheme();
  if (getDocumentTheme() !== expected || document.documentElement.style.colorScheme !== expected) {
    applyTheme(expected);
  }
  document.documentElement.dataset.theme = expected;
};
